/**
 * Allergy-vs-prescription safety matching.
 *
 * Pure, dependency-light logic that flags when a prescribed line references a
 * substance the patient is documented as allergic to. Kept free of React /
 * PowerSync so it can be unit-tested and reused (see allergy-check.test.ts).
 *
 * Matching is deliberately conservative — a normalized, word-boundary match of
 * the whole allergen substance against the line's display name, free-text name,
 * and inventory snapshots (item / brand / generic). We do NOT do fuzzy or
 * drug-class matching in v1: false alarms erode trust in the warning, so we only
 * fire when the prescribed text literally contains the allergen.
 */

export type AllergySeverity = 'severe' | 'moderate' | 'mild' | 'unknown';

/** Minimal shape of a patient allergy row (matches PatientAllergyRow). */
export interface AllergyLike {
  allergy_type: string | null;
  substance: string | null;
  reaction: string | null;
  severity: string | null;
}

/** The name-bearing fields of a prescription line we match against. */
export interface PrescriptionNameFields {
  itemName?: string | null;
  freeTextName?: string | null;
  itemNameSnapshot?: string | null;
  brandNameSnapshot?: string | null;
  genericNameSnapshot?: string | null;
}

export interface AllergyConflict {
  substance: string;
  severity: AllergySeverity;
  reaction: string | null;
}

/** Lowercase, strip punctuation to spaces, collapse whitespace. */
export function normalizeName(value: string | null | undefined): string {
  return (value ?? '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

export function normalizeSeverity(value: string | null | undefined): AllergySeverity {
  const n = (value ?? '').toLowerCase().trim();
  if (n.startsWith('sev')) return 'severe';
  if (n.startsWith('mod')) return 'moderate';
  if (n.startsWith('mil')) return 'mild';
  return 'unknown';
}

const SEVERITY_RANK: Record<AllergySeverity, number> = {
  severe: 0,
  moderate: 1,
  mild: 2,
  unknown: 3,
};

/**
 * True when the whole allergen `substance` appears as a word-boundary token
 * sequence within any of the prescription name fields. Word boundaries prevent
 * "egg" from matching "eggmycin" while still matching "aspirin" in "aspirin
 * 150mg" style names.
 */
export function nameMatchesSubstance(
  names: PrescriptionNameFields,
  substance: string | null | undefined,
): boolean {
  const needle = normalizeName(substance);
  if (!needle) return false;
  const padded = ` ${needle} `;
  const candidates = [
    names.itemName,
    names.freeTextName,
    names.itemNameSnapshot,
    names.brandNameSnapshot,
    names.genericNameSnapshot,
  ];
  for (const c of candidates) {
    const hay = normalizeName(c);
    if (hay && ` ${hay} `.includes(padded)) return true;
  }
  return false;
}

/** Food allergies are excluded from prescription checks; drug/medication and
 *  unspecified (null) types are included. */
function isPrescriptionRelevant(allergyType: string | null): boolean {
  const t = (allergyType ?? '').toLowerCase().trim();
  if (!t) return true; // unspecified — keep (medicine names rarely collide with food words)
  return t === 'drug' || t === 'medication' || t === 'medicine';
}

/**
 * Return the documented allergies the prescribed line conflicts with, most
 * severe first, de-duplicated by substance.
 */
export function findAllergyConflicts(
  names: PrescriptionNameFields,
  allergies: AllergyLike[],
): AllergyConflict[] {
  const out: AllergyConflict[] = [];
  const seen = new Set<string>();
  for (const a of allergies) {
    if (!a.substance) continue;
    if (!isPrescriptionRelevant(a.allergy_type)) continue;
    if (!nameMatchesSubstance(names, a.substance)) continue;
    const key = normalizeName(a.substance);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({
      substance: a.substance,
      severity: normalizeSeverity(a.severity),
      reaction: a.reaction,
    });
  }
  return out.sort((x, y) => SEVERITY_RANK[x.severity] - SEVERITY_RANK[y.severity]);
}

/** Highest severity among a set of conflicts (for tone/color). */
export function highestSeverity(conflicts: AllergyConflict[]): AllergySeverity {
  let worst: AllergySeverity = 'unknown';
  for (const c of conflicts) {
    if (SEVERITY_RANK[c.severity] < SEVERITY_RANK[worst]) worst = c.severity;
  }
  return worst;
}

/**
 * Stable key identifying a line's specific conflict set, so an in-session
 * acknowledgement is invalidated if the prescriber later changes the line to a
 * different allergen.
 */
export function conflictAckKey(draftId: string, conflicts: AllergyConflict[]): string {
  const subs = conflicts
    .map((c) => normalizeName(c.substance))
    .sort()
    .join(',');
  return `${draftId}|${subs}`;
}
